import { useState, useEffect, useCallback } from 'react';
import type { TOptions } from 'i18next';
import i18n from '../utils/i18n';
import { useI18nContext } from '../components/I18nWrapper';

export const useSafeTranslation = () => {
  const context = useI18nContext();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const checkReady = () => {
      if (i18n.isInitialized) {
        setIsReady(true);
      }
    };

    // Check immediately
    checkReady();

    // Listen for initialization
    const handleInitialized = () => {
      setIsReady(true);
    };

    i18n.on('initialized', handleInitialized);

    return () => {
      i18n.off('initialized', handleInitialized);
    };
  }, []);

  const t = useCallback((key: string, fallbackOrOptions?: string | TOptions) => {
    try {
      // Do not gate on React readiness: i18n is a singleton and can run before I18nWrapper/useEffect
      // finish; otherwise fast paths (e.g. PvP lobby load error) always show the English fallback.
      if (!i18n.isInitialized) {
        if (typeof fallbackOrOptions === 'string') {
          return fallbackOrOptions;
        }
        if (fallbackOrOptions && typeof fallbackOrOptions === 'object' && 'defaultValue' in fallbackOrOptions) {
          const dv = (fallbackOrOptions as TOptions & { defaultValue?: string }).defaultValue;
          return typeof dv === 'string' ? dv : key;
        }
        return key;
      }

      if (typeof fallbackOrOptions === 'string') {
        const translation = i18n.t(key, { defaultValue: fallbackOrOptions });
        if (translation === undefined || translation === null || translation === key) {
          return fallbackOrOptions;
        }
        return translation;
      }

      if (fallbackOrOptions !== undefined && typeof fallbackOrOptions === 'object') {
        const translation = i18n.t(key, fallbackOrOptions);
        const objFallback =
          'defaultValue' in fallbackOrOptions
            ? (fallbackOrOptions as TOptions & { defaultValue?: string }).defaultValue
            : undefined;
        if (translation === undefined || translation === null || translation === key) {
          return objFallback !== undefined ? objFallback : key;
        }
        return translation;
      }

      const translation = i18n.t(key);
      if (translation === undefined || translation === null || translation === key) {
        return key;
      }
      return translation;
    } catch {
      if (typeof fallbackOrOptions === 'string') {
        return fallbackOrOptions;
      }
      if (fallbackOrOptions && typeof fallbackOrOptions === 'object' && 'defaultValue' in fallbackOrOptions) {
        const dv = (fallbackOrOptions as TOptions & { defaultValue?: string }).defaultValue;
        return dv !== undefined ? dv : key;
      }
      return key;
    }
  }, []);

  return {
    t,
    ready: isReady && context.isReady,
    i18n,
    currentLanguage: context.currentLanguage,
  };
}; 